
 /*!
 * TEMA DESENVOLVIDO POR SABINOv LTDA.
 * O USO INDEVIDO DO TEMA RESULTARÁ EM DMCA POR DIREITOS DO TEMA
 * @license Proprietary
 * @preserve
 */






import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    // O upsell do carrinho pode ser re-renderizado por morphing. O listener fica
    // neste módulo permanente do drawer (e não dentro do snippet Liquid), para
    // funcionar também no primeiro acesso/anônimo e após qualquer atualização.
    document.addEventListener('click', this.#handleAqualizaUpsellAdd, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('click', this.#handleAqualizaUpsellAdd, true);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  #handleAqualizaUpsellAdd = async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-aqualiza-upsell-add]');
    if (!(button instanceof HTMLButtonElement)) return;
    if (!this.contains(button)) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (button.disabled || button.getAttribute('aria-busy') === 'true') return;

    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    const text = button.querySelector('.cart-one-click-offer__button-text');
    const originalText = text?.textContent || 'ADICIONAR';
    const root = window.Shopify?.routes?.root || '/';
    const cartRoot = root.endsWith('/') ? root : `${root}/`;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (text) text.textContent = 'ADICIONANDO...';

    try {
      const addResponse = await fetch(`${cartRoot}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: {
                _aqualiza_no_progressive_discount: 'true',
              },
            },
          ],
        }),
      });

      if (!addResponse.ok) {
        throw new Error(`Falha ao adicionar ao carrinho: ${addResponse.status}`);
      }

      if (text) text.textContent = 'ADICIONADO';

      const cartResponse = await fetch(`${cartRoot}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!cartResponse.ok) {
        throw new Error(`Falha ao atualizar carrinho: ${cartResponse.status}`);
      }

      const cart = await cartResponse.json();

      // Usa o evento nativo do tema. O cart-items-component recebe o evento e
      // busca/renderiza novamente a seção, fazendo a oferta sumir e os totais
      // atualizarem sem depender de scripts inseridos via morphing.
      document.dispatchEvent(
        new CartAddEvent(cart, 'aqualiza-one-click-offer', {
          source: 'aqualiza-one-click-offer',
          itemCount: Number(cart.item_count) || 0,
          variantId: String(variantId),
        })
      );
    } catch (error) {
      console.error('Aqualiza upsell:', error);
      if (text) text.textContent = 'TENTE NOVAMENTE';

      window.setTimeout(() => {
        if (!button.isConnected) return;
        if (text) text.textContent = originalText;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }, 1600);
      return;
    }

    // Se o morphing ainda não tiver substituído o botão, mantém bloqueado até
    // a nova seção chegar para impedir duplo clique/adicionamento duplicado.
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
